import React from "react";
import styled from "styled-components";
import MediaQuery from "react-responsive";
import { Link } from "react-router-dom";

import Header from "../Header";
import Footer from "../Footer";
import { Layout } from "../styled";
import theme from "../styled/theme";

const LAST_UPDATED = "12 de mayo de 2026";
const CONTACT = {
  phone: "809 879 7427",
  email: "contact@bambansushi.com",
  address:
    "Av. Gustavo M. Ricart #59, Naco; entre Lope de Vega y Tiradentes, Santo Domingo, R.D.",
};

const LEGAL_LINKS = [
  { path: "/legal/security", label: "Seguridad" },
  { path: "/legal/refunds", label: "Reembolsos" },
  { path: "/legal/delivery", label: "Entrega" },
  { path: "/legal/terms", label: "Términos" },
  { path: "/legal/privacy", label: "Privacidad" },
  { path: "/legal/cookies", label: "Cookies" },
];

const PAGES = {
  security: {
    title: "Política de Seguridad",
    intro:
      "En Bamban Sushi tomamos medidas razonables para proteger la información personal que recibimos a través del sitio y durante el proceso de orden. Esta política resume cómo resguardamos la información y cuáles son los límites de ese resguardo.",
    sections: [
      {
        title: "Transmisión segura",
        paragraphs: [
          "Procuramos que las comunicaciones entre tu navegador y nuestros servicios se realicen mediante conexiones seguras y controles técnicos apropiados.",
        ],
      },
      {
        title: "Información que tratamos",
        paragraphs: [
          "Tratamos solamente la información necesaria para recibir y gestionar tu orden, contactarte por WhatsApp, correo o teléfono, y coordinar entrega, takeout o consumo en restaurante.",
        ],
        bullets: [
          "Datos de contacto y nombre del cliente.",
          "Dirección o referencias de entrega cuando aplique.",
          "Detalles de la orden y comentarios del cliente.",
        ],
      },
      {
        title: "Acceso y uso interno",
        paragraphs: [
          "El acceso a la información se limita al personal y proveedores que la necesitan para operar el servicio, atender incidencias o cumplir obligaciones legales.",
        ],
      },
      {
        title: "Pagos",
        paragraphs: [
          "Las formas de pago pueden incluir efectivo, transferencia o tarjeta, según disponibilidad operativa. Bamban Sushi no solicita que compartas datos sensibles de tarjeta por redes sociales abiertas y recomienda confirmar cualquier instrucción de pago por nuestros canales oficiales.",
        ],
      },
      {
        title: "Recomendaciones al cliente",
        bullets: [
          "Verifica que estás en `bambansushi.com` antes de enviar datos.",
          "No compartas información financiera sensible por mensajes públicos.",
          "Contáctanos de inmediato si detectas actividad o mensajes sospechosos relacionados con tu orden.",
        ],
      },
    ],
  },
  refunds: {
    title: "Política de Devoluciones, Reembolsos y Cancelaciones",
    intro:
      "Nuestros productos son preparados al momento y son perecederos. Por esa naturaleza, las devoluciones y reembolsos están sujetos a condiciones específicas.",
    sections: [
      {
        title: "Cancelaciones",
        paragraphs: [
          "Puedes solicitar la cancelación de una orden antes de que entre en preparación. Una vez iniciada la preparación, la orden no podrá cancelarse ni reembolsarse salvo que exista un error atribuible a Bamban Sushi.",
        ],
      },
      {
        title: "Órdenes incorrectas o incompletas",
        paragraphs: [
          "Si recibes un producto incorrecto o incompleto, debes reportarlo tan pronto como sea posible el mismo día de la entrega o retiro para evaluar una reposición, ajuste o crédito.",
        ],
      },
      {
        title: "Calidad del producto",
        paragraphs: [
          "Si entiendes que un producto llegó en malas condiciones, contáctanos de inmediato con tu número de orden, descripción del caso y, de ser posible, evidencia fotográfica.",
        ],
      },
      {
        title: "Reembolsos aplicables",
        paragraphs: [
          "Los reembolsos se evalúan caso por caso y aplican únicamente cuando la orden no pueda ser entregada o corregida razonablemente, o cuando el problema sea atribuible a Bamban Sushi.",
        ],
        bullets: [
          "El método y tiempo de reembolso puede depender del medio de pago utilizado.",
          "Cargos o comisiones de terceros fuera de nuestro control podrían no ser reembolsables.",
        ],
      },
      {
        title: "Exclusiones",
        bullets: [
          "No aceptamos devoluciones por cambio de opinión luego de preparada la orden.",
          "No aplica reembolso sobre productos parcialmente consumidos, salvo validación por defecto de calidad.",
        ],
      },
    ],
  },
  delivery: {
    title: "Política de Entrega y Takeout",
    intro:
      "Esta política explica cómo funcionan las órdenes para entrega, takeout y, cuando aplique, consumo en restaurante a través de Bamban Sushi.",
    sections: [
      {
        title: "Takeout",
        paragraphs: [
          "Si eliges pasar a buscar, recibirás una confirmación de la orden y el tiempo estimado de preparación. Recomendamos retirar dentro del tiempo indicado para preservar la calidad del producto.",
        ],
      },
      {
        title: "Delivery",
        paragraphs: [
          "El servicio de entrega está sujeto a cobertura, horario operativo, volumen de órdenes y validación de la dirección. En algunos casos podremos contactarte por WhatsApp para confirmar ubicación, referencias o disponibilidad.",
        ],
        bullets: [
          "El monto mínimo y cargos de entrega pueden variar según la operación vigente.",
          "Los tiempos informados son estimados y pueden cambiar por tráfico, clima o alta demanda.",
        ],
      },
      {
        title: "Intentos de contacto",
        paragraphs: [
          "Si no logramos confirmarte o entregarte la orden por falta de respuesta, dirección incompleta o imposibilidad de acceso, haremos esfuerzos razonables para contactarte antes de cerrar el caso.",
        ],
      },
      {
        title: "Cambios en la modalidad",
        paragraphs: [
          "Si necesitas cambiar de delivery a takeout o viceversa, debes avisarnos antes de que la orden entre en preparación. Haremos lo posible por ayudarte, pero no podemos garantizar cambios tardíos.",
        ],
      },
    ],
  },
  terms: {
    title: "Términos y Condiciones",
    intro:
      "Estos Términos y Condiciones regulan el acceso y uso del sitio web de Bamban Sushi y de los servicios asociados a órdenes en línea.",
    sections: [
      {
        title: "Aceptación",
        paragraphs: [
          "Al usar `bambansushi.com`, aceptas estos términos. Si no estás de acuerdo, debes abstenerte de utilizar el sitio.",
        ],
      },
      {
        title: "Titular del sitio",
        paragraphs: [
          "El sitio es operado por Crosswall Investments SRL para la marca Bamban Sushi.",
        ],
      },
      {
        title: "Uso permitido",
        bullets: [
          "Brindar información veraz al realizar una orden o consulta.",
          "Usar el sitio conforme a la ley y a la buena fe.",
          "No intentar acceder a áreas restringidas ni interferir con la operación del sitio.",
        ],
      },
      {
        title: "Productos, precios y disponibilidad",
        paragraphs: [
          "Los productos, precios, ingredientes, promociones y disponibilidad pueden cambiar sin previo aviso. Las imágenes son referenciales y la presentación final puede variar.",
        ],
      },
      {
        title: "Alergias y restricciones alimentarias",
        paragraphs: [
          "Si tienes alergias o restricciones alimentarias, debes indicarlo antes de confirmar tu orden. Aunque procuramos orientar sobre ingredientes, no garantizamos ausencia total de trazas o contaminación cruzada.",
        ],
      },
      {
        title: "Órdenes y pagos",
        paragraphs: [
          "Las órdenes recibidas quedan sujetas a validación operativa. Las condiciones de pago, entrega, cancelación y reembolso se complementan con las políticas publicadas en esta sección legal.",
        ],
      },
      {
        title: "Propiedad intelectual",
        paragraphs: [
          "El contenido del sitio, incluyendo textos, logotipos, imágenes, diseños y software, pertenece a Bamban Sushi o a sus licenciantes y no puede reproducirse sin autorización.",
        ],
      },
      {
        title: "Responsabilidad",
        paragraphs: [
          "No garantizamos que el sitio esté libre de interrupciones o errores. En la medida permitida por la ley, Bamban Sushi no será responsable por daños indirectos derivados del uso del sitio o de indisponibilidades temporales.",
        ],
      },
      {
        title: "Ley aplicable",
        paragraphs: [
          "Estos términos se interpretan conforme a las leyes de la República Dominicana. Cualquier controversia será conocida por los tribunales competentes del Distrito Nacional, salvo norma imperativa en contrario.",
        ],
      },
    ],
  },
  privacy: {
    title: "Política de Privacidad",
    intro:
      "En Bamban Sushi respetamos tu privacidad. Esta política explica qué información podemos recopilar, cómo la usamos y cuáles son tus derechos respecto de esos datos.",
    sections: [
      {
        title: "Datos que recopilamos",
        bullets: [
          "Nombre, correo, teléfono y demás datos que nos compartes al ordenar o contactarnos.",
          "Dirección, referencias y comentarios de entrega cuando aplican.",
          "Información técnica básica del navegador o dispositivo para fines operativos y estadísticos.",
        ],
      },
      {
        title: "Finalidades del tratamiento",
        bullets: [
          "Procesar órdenes y coordinar su entrega, retiro o consumo.",
          "Dar seguimiento por WhatsApp, teléfono o correo.",
          "Mejorar la operación del sitio y atender incidencias.",
          "Cumplir obligaciones legales, contables y administrativas.",
        ],
      },
      {
        title: "Base de uso",
        paragraphs: [
          "Usamos la información porque es necesaria para prestarte el servicio solicitado, para responder tus consultas, para cumplir obligaciones legales y, cuando corresponda, con base en tu consentimiento.",
        ],
      },
      {
        title: "Compartición",
        paragraphs: [
          "No vendemos tus datos personales. Podemos compartir información con proveedores que nos apoyan en hosting, mensajería, analítica, soporte o gestión operativa, bajo condiciones razonables de confidencialidad.",
        ],
      },
      {
        title: "Conservación",
        paragraphs: [
          "Conservamos la información por el tiempo necesario para operar el servicio, resolver reclamaciones y cumplir obligaciones legales o administrativas.",
        ],
      },
      {
        title: "Tus derechos",
        bullets: [
          "Solicitar acceso a tu información.",
          "Pedir corrección o actualización de datos inexactos.",
          "Solicitar eliminación cuando proceda legalmente.",
          "Oponerte a determinados usos o retirar consentimientos otorgados.",
        ],
      },
      {
        title: "Seguridad",
        paragraphs: [
          "Aplicamos medidas técnicas y organizativas razonables para proteger la información contra acceso no autorizado, alteración, divulgación o pérdida.",
        ],
      },
      {
        title: "Menores de edad",
        paragraphs: [
          "El sitio no está dirigido intencionalmente a menores de 13 años. Si entiendes que un menor nos ha compartido datos sin autorización, contáctanos para revisarlo.",
        ],
      },
    ],
  },
  cookies: {
    title: "Política de Cookies",
    intro:
      "Esta política describe qué son las cookies, cómo pueden utilizarse en `bambansushi.com` y qué opciones tienes para gestionarlas.",
    sections: [
      {
        title: "Qué son las cookies",
        paragraphs: [
          "Las cookies son pequeños archivos de texto que un sitio web guarda en tu dispositivo para recordar información sobre tu visita y facilitar ciertas funciones.",
        ],
      },
      {
        title: "Cómo las usamos",
        paragraphs: [
          "Podemos utilizar cookies o tecnologías similares para mantener funciones básicas del sitio, entender el uso general de la plataforma y mejorar la experiencia del usuario.",
        ],
      },
      {
        title: "Tipos de cookies",
        bullets: [
          "Necesarias: permiten funciones esenciales del sitio.",
          "Analíticas o de rendimiento: ayudan a entender tráfico y uso general.",
          "Funcionales: recuerdan ciertas preferencias o estados de navegación.",
        ],
      },
      {
        title: "Terceros",
        paragraphs: [
          "Algunas herramientas de terceros, como servicios de analítica, mapas, mensajería o contenido embebido, pueden instalar sus propias cookies bajo sus políticas respectivas.",
        ],
      },
      {
        title: "Cómo controlarlas",
        paragraphs: [
          "Puedes eliminar o bloquear cookies desde la configuración de tu navegador. Ten en cuenta que al hacerlo algunas funciones del sitio podrían dejar de operar correctamente.",
        ],
      },
      {
        title: "Consentimiento",
        paragraphs: [
          "Al continuar navegando en el sitio, entiendes que pueden utilizarse cookies necesarias y otras tecnologías de operación conforme a esta política y a la configuración de tu navegador.",
        ],
      },
    ],
  },
};

const Page = styled.div`
  min-height: 100vh;
`;

const LegalShell = styled.div`
  margin: 0 auto;
  max-width: 920px;
  padding-top: 0.5rem;

  @media (min-width: 768px) {
    padding-top: 1.25rem;
  }
`;

const UtilityBar = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
  margin: 0 auto 1rem;
  max-width: 880px;
`;

const BrandPill = styled(Link)`
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(161, 2, 4, 0.18);
  border-radius: 999px;
  color: ${theme.colors.grayDark};
  display: inline-flex;
  font-family: "Chunq";
  font-size: 1rem;
  letter-spacing: 0.03em;
  padding: 0.7rem 1rem;
  text-decoration: none;
  text-transform: uppercase;
`;

const HomeLink = styled(Link)`
  background: ${theme.colors.primary};
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 0.95rem;
  font-weight: bold;
  padding: 0.75rem 1rem;
  text-decoration: none;
`;

const Nav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: center;
  margin: 0 auto 1.25rem;
  max-width: 880px;
`;

const NavLink = styled(Link)`
  background: ${props => (props.$active ? theme.colors.primary : "rgba(255,255,255,0.88)")};
  border: 1px solid ${theme.colors.primary};
  border-radius: 999px;
  color: ${props => (props.$active ? "#fff" : theme.colors.primary)};
  font-family: Arial, Helvetica, sans-serif;
  font-size: 0.95rem;
  padding: 0.7rem 1rem;
  text-decoration: none;
`;

const Panel = styled.article`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 18px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
  max-width: 880px;
  padding: 1.5rem;

  @media (min-width: 768px) {
    padding: 2.5rem 3rem;
  }
`;

const BrandLink = styled(Link)`
  color: ${theme.colors.primary};
  display: inline-block;
  font-family: "Chunq";
  font-size: 1.2rem;
  letter-spacing: 0.03em;
  margin-bottom: 0.75rem;
  text-decoration: none;
  text-transform: uppercase;
`;

const Heading = styled.h1`
  color: ${theme.colors.grayDark};
  font-family: "Chunq";
  font-size: 2.35rem;
  line-height: 1;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const UpdatedAt = styled.p`
  color: ${theme.colors.gray};
  font-family: Arial, Helvetica, sans-serif;
  font-size: 0.95rem;
  margin: 0.75rem 0 1.25rem;
`;

const Intro = styled.p`
  color: ${theme.colors.grayDark};
  font-family: Arial, Helvetica, sans-serif;
  font-size: 1rem;
  line-height: 1.7;
  margin: 0 0 1.5rem;
`;

const Section = styled.section`
  border-top: 1px solid rgba(55, 53, 53, 0.12);
  padding-top: 1.25rem;

  & + & {
    margin-top: 1.25rem;
  }
`;

const SectionTitle = styled.h2`
  color: ${theme.colors.primary};
  font-family: Arial, Helvetica, sans-serif;
  font-size: 1.15rem;
  margin: 0 0 0.75rem;
  text-transform: uppercase;
`;

const Text = styled.p`
  color: ${theme.colors.grayDark};
  font-family: Arial, Helvetica, sans-serif;
  font-size: 1rem;
  line-height: 1.7;
  margin: 0 0 0.75rem;
`;

const BulletList = styled.ul`
  color: ${theme.colors.grayDark};
  font-family: Arial, Helvetica, sans-serif;
  font-size: 1rem;
  line-height: 1.7;
  margin: 0.25rem 0 0;
  padding-left: 1.2rem;
`;

const ContactBox = styled.section`
  background: ${theme.colors.icecream};
  border: 1px solid rgba(161, 2, 4, 0.12);
  border-radius: 14px;
  margin-top: 1.75rem;
  padding: 1rem 1.1rem;
`;

const ContactTitle = styled.h3`
  color: ${theme.colors.primary};
  font-family: Arial, Helvetica, sans-serif;
  font-size: 1rem;
  margin: 0 0 0.65rem;
  text-transform: uppercase;
`;

const ContactLine = styled.p`
  color: ${theme.colors.grayDark};
  font-family: Arial, Helvetica, sans-serif;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0.2rem 0;
`;

const LegalPage = ({ pageKey }) => {
  const page = PAGES[pageKey];

  if (!page) return null;

  return (
    <Page>
      <MediaQuery maxDeviceWidth={767}>
        <Header hideCart />
      </MediaQuery>
      <Layout>
        <LegalShell>
          <UtilityBar>
            <BrandPill to="/">Bamban Sushi</BrandPill>
            <HomeLink to="/">Ir a inicio</HomeLink>
          </UtilityBar>
          <Nav>
            {LEGAL_LINKS.map(link => (
              <NavLink key={link.path} to={link.path} $active={link.path === `/legal/${pageKey}`}>
                {link.label}
              </NavLink>
            ))}
          </Nav>
          <Panel>
            <BrandLink to="/">Bamban Sushi</BrandLink>
            <Heading>{page.title}</Heading>
            <UpdatedAt>Última actualización: {LAST_UPDATED}</UpdatedAt>
            <Intro>{page.intro}</Intro>

            {page.sections.map((section, index) => (
              <Section key={section.title}>
                <SectionTitle>
                  {index + 1}. {section.title}
                </SectionTitle>
                {section.paragraphs &&
                  section.paragraphs.map(paragraph => <Text key={paragraph}>{paragraph}</Text>)}
                {section.bullets && (
                  <BulletList>
                    {section.bullets.map(bullet => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </BulletList>
                )}
              </Section>
            ))}

            <ContactBox>
              <ContactTitle>Información de contacto</ContactTitle>
              <ContactLine>Email: {CONTACT.email}</ContactLine>
              <ContactLine>Teléfono / WhatsApp: {CONTACT.phone}</ContactLine>
              <ContactLine>Dirección: {CONTACT.address}</ContactLine>
            </ContactBox>
          </Panel>
        </LegalShell>
        <Footer />
      </Layout>
    </Page>
  );
};

export const SecurityPolicyPage = () => <LegalPage pageKey="security" />;
export const RefundsPolicyPage = () => <LegalPage pageKey="refunds" />;
export const DeliveryPolicyPage = () => <LegalPage pageKey="delivery" />;
export const TermsPage = () => <LegalPage pageKey="terms" />;
export const PrivacyPolicyPage = () => <LegalPage pageKey="privacy" />;
export const CookiesPolicyPage = () => <LegalPage pageKey="cookies" />;
